import React, { Component } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { RootState } from '../../store'
import imageService from '../../services/image.service'
import {
  Element,
  addImageElement,
  addTextElement,
  addBarcodeElement,
  addRfidElement,
  addGs1BarcodeElement,
  addLineElement,
  addCircleElement,
  addRectElement,
  addEllipseElement,
  addWedgeElement,
  addRingElement,
  addArcElement,
  addArrowElement
} from '../../views/LabelBuilder/LabelBuilder.slice'
import sampleImage from '../../assets/label-sample-image.png'
import barcodePlaceholderImage from '../../assets/label-barcode-placeholder.png'

import imageSvg from '../../assets/image.svg'
import textSvg from '../../assets/text.svg'
import barcodeSvg from '../../assets/barcode.svg'
import rfidSvg from '../../assets/rfid.svg'
import gs1BarcodeSvg from '../../assets/gs1-barcode.svg'
import gs1Api from '../../apis/gs1.api'
import gs1BarcodeService from '../../services/gs1Barcode.service'
import lineSvg from '../../assets/line.svg'
import circleSvg from '../../assets/circle.svg'
import rectSvg from '../../assets/rect.svg'
import ellipseSvg from '../../assets/ellipse.svg'
import wedgeSvg from '../../assets/wedge.svg'
import ringSvg from '../../assets/ring.svg'
import arcSvg from '../../assets/arc.svg'
import arrowSvg from '../../assets/arrow.svg'
import Card from '../Card'

interface Props {
}

type CombinedProps = Props & ConnectedProps<typeof connector>
type PickableElementType = Exclude<Element['type'], 'group'>

interface State {
  gs1Enabled: boolean
  gs1DictionaryVersion?: string
}

class ElementPicker extends Component<CombinedProps, State> {
  state: State = { gs1Enabled: false }

  componentDidMount () {
    // The GS1 Barcode tile is behind a per-account feature flag.
    gs1Api.catalog(catalog => {
      if (catalog) this.setState({ gs1Enabled: catalog.enabled, gs1DictionaryVersion: catalog.dictionaryVersion })
    })
  }

  titles: Record<PickableElementType, string> = {
    image: 'Image',
    text: 'Text',
    barcode: '1D/2D barcode',
    rfid: 'RFID',
    gs1_barcode: 'GS1 barcode',
    line: 'Line',
    circle: 'Circle',
    rect: 'Rect',
    ellipse: 'Ellipse',
    wedge: 'Wedge',
    ring: 'Ring',
    arc: 'Arc',
    arrow: 'Arrow'
  }

  handleAddClick = (type: PickableElementType) => {
    switch (type) {
      case 'image': {
        imageService.generateImage(sampleImage, (image) => {
          this.props.addImageElement(image)
        })
        break
      }
      case 'text': {
        this.props.addTextElement()
        break
      }
      case 'barcode': {
        imageService.generateImage(barcodePlaceholderImage, (image) => {
          this.props.addBarcodeElement(image)
        })
        break
      }
      case 'rfid': {
        this.props.addRfidElement()
        break
      }
      case 'gs1_barcode': {
        this.props.addGs1BarcodeElement(gs1BarcodeService.defaultConfig(this.state.gs1DictionaryVersion))
        break
      }
      case 'line': {
        this.props.addLineElement()
        break
      }
      case 'circle': {
        this.props.addCircleElement()
        break
      }
      case 'rect': {
        this.props.addRectElement()
        break
      }
      case 'ellipse': {
        this.props.addEllipseElement()
        break
      }
      case 'wedge': {
        this.props.addWedgeElement()
        break
      }
      case 'arc': {
        this.props.addArcElement()
        break
      }
      case 'ring': {
        this.props.addRingElement()
        break
      }
      case 'arrow': {
        this.props.addArrowElement()
        break
      }
    }
  }

  iconMap: Record<PickableElementType, string> = {
    image: imageSvg,
    text: textSvg,
    barcode: barcodeSvg,
    rfid: rfidSvg,
    gs1_barcode: gs1BarcodeSvg,
    line: lineSvg,
    circle: circleSvg,
    rect: rectSvg,
    ellipse: ellipseSvg,
    wedge: wedgeSvg,
    ring: ringSvg,
    arc: arcSvg,
    arrow: arrowSvg
  }

  /**
   * Tray order.
   *
   * With GS1 Barcode on: Text, Image, GS1 Barcode, Standard Barcode. At four
   * tiles to a row that is one row, and it puts the two barcode tiles side
   * by side where they can be compared.
   *
   * The legacy RFID tile is left out of that tray: a GS1 Barcode writes the
   * label's tag from its own data, and a label can only have one writer.
   * Only the tile goes - labels that already carry an RFID element keep it,
   * and accounts without GS1 keep the tray exactly as it was.
   */
  get trayOrder (): PickableElementType[] {
    const shapes: PickableElementType[] = ['line', 'circle', 'rect', 'ellipse', 'wedge', 'ring', 'arc', 'arrow']
    if (!this.state.gs1Enabled) {
      return ['image', 'text', 'barcode', 'rfid', ...shapes]
    }
    return ['text', 'image', 'gs1_barcode', 'barcode', ...shapes]
  }

  element (type: PickableElementType) {
    const selectedIcon = this.iconMap[type] || imageSvg

    return (

            <Card background="darker" flat={true}>
                <div className='justify-center items-center cursor-pointer text-center' style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }} onClick={() => this.handleAddClick(type)}>
                    <img src={selectedIcon} alt={type} title={type === 'barcode' && this.state.gs1Enabled ? '1D/2D barcode (legacy)' : this.titles[type]} width={38} height={38}/>
                </div>
            </Card>
    )
  }

  render () {
    return (
            <div className='mb-4'>
                {/* <Heading */}
                {/*    className="mb-2" */}
                {/*    text={i18n('Elements')} */}
                {/*    size="small"/> */}
                <div className="grid grid-cols-4 gap-2">
                    {this.trayOrder.map(type => <React.Fragment key={type}>{this.element(type)}</React.Fragment>)}
                </div>
            </div>
    )
  }
}

function mapStateTopProps (state: RootState, props: Props) {
  return {}
}

const mapDispatchToProps = {
  addImageElement,
  addTextElement,
  addBarcodeElement,
  addRfidElement,
  addGs1BarcodeElement,
  addLineElement,
  addCircleElement,
  addRectElement,
  addEllipseElement,
  addWedgeElement,
  addRingElement,
  addArcElement,
  addArrowElement
}

const connector = connect(mapStateTopProps, mapDispatchToProps)
export default connector(ElementPicker)
